import { Button, IconButton, InputAdornment, TextField } from '@mui/material';
import React, { Dispatch, SetStateAction, SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { CloseIcon, FilledFilterIcon, FilterIcon, SearchIcon } from './AppIcons';
import { debounce } from 'lodash';

const useStyles = makeStyles({
  textField: {
    height: '40px',
    '& input::placeholder': {
      color: '#30343F  !important',
    },
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused fieldset': {
        borderWidth: '1px !important',
        borderColor: `#E1E3E8 !important`,
      },
    },
  },
  input: {
    height: '40px',
    padding: '0 !important',
    fontFamily: 'Lato !important',
    '& .MuiInputAdornment-root': {
      marginRight: 0,
    },
  },
  innerText: {
    fontSize: '14px !important',
    textOverflow: 'ellipsis',
    overflow: 'hidden !important',
    fontFamily: 'Lato !important',
    color: '#30343F  !important',
    '& .Mui-focused': {
      fontSize: 14,
    },
  },
  filtersButton: {
    fontSize: '14px !important',
    height: '40px',
    width: '115px',
    padding: '0 !important',
    justifyContent: 'flex-start !important',
    marginLeft: '20px !important',
    marginRight: '20px !important',
    '& path': {
      fill: `inherit !important`,
    },
    '& .MuiButton-startIcon': {
      margin: 0,
    },
  },
  filterIcon: {
    color: '#2863EB !important',
  },
  filtersCount: {
    backgroundColor: '#1264E5',
    width: '20px',
    height: '20px',
    color: '#fff',
    borderRadius: '50%',
    fontSize: '12px !important',
  },
  close: {
    fontSize: '50px !important',
    color: '#5F6774 !important',
    '& path': {
      fill: `#2C2C2C !important`,
    },
  },
});

interface Props {
  query?: string;
  width?: number;
  onSubmit: (q: string) => void;
  isFiltersPresent?: boolean;
  openFilters?: boolean;
  filtersCount?: number;
  setOpenFilters?: Dispatch<SetStateAction<boolean>>;
  setFiltersAnchorEl?: (event: React.MouseEvent<HTMLElement>) => void;
  placeholder?: string;
}

const SearchBox: React.FC<Props> = ({
  query,
  width,
  onSubmit,
  openFilters,
  filtersCount = 0,
  isFiltersPresent,
  setOpenFilters,
  setFiltersAnchorEl,
  placeholder,
}) => {
  const classes = useStyles();
  const [searchText, setSearchText] = useState('');

  const debouncedSubmit = useMemo(() => debounce((q: string) => onSubmit(q), 300), [onSubmit]);

  useEffect(() => {
    return () => {
      debouncedSubmit.cancel();
    };
  }, [debouncedSubmit]);

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    if (setOpenFilters && setFiltersAnchorEl) {
      setOpenFilters(true);
      setFiltersAnchorEl(event);
    }
  };

  const handleFormSubmit = (event: SyntheticEvent) => {
    event.preventDefault();
    onSubmit(searchText);
  };

  function handleSearchClick() {
    onSubmit(searchText);
  }

  function handleClearSearch() {
    setSearchText('');
    onSubmit('');
  }

  return (
    <form onSubmit={handleFormSubmit} style={{ fontFamily: 'Lato !important' }}>
      <TextField
        value={searchText}
        className={classes.textField}
        sx={{ width: width || '350px' }}
        variant="outlined"
        focused
        placeholder={placeholder || 'Search'}
        id="search-box"
        onKeyPress={(e: any) => {
          if (e.key === 'Enter') {
            handleSearchClick();
            e.preventDefault();
          }
        }}
        onChange={(e) => {
          const value = e.target.value;
          setSearchText(value);
          debouncedSubmit(value);
        }}
        InputProps={{
          className: classes.input,
          classes: { input: classes.innerText },
          startAdornment: (
            <>
              <InputAdornment position="start">
                <IconButton onClick={handleSearchClick} role="button" style={{ padding: '0' }}>
                  <SearchIcon viewBox="0 0 40 40" sx={{ width: '40px', height: '40px' }} />
                </IconButton>
              </InputAdornment>
            </>
          ),
          endAdornment: (
            <>
              {query && query.length !== 0 && searchText === query && (
                <InputAdornment position="end">
                  <IconButton onClick={handleClearSearch} role="button">
                    <CloseIcon viewBox="0 0 42 42" className={classes.close} />
                  </IconButton>
                </InputAdornment>
              )}
            </>
          ),
        }}
        InputLabelProps={{
          className: classes.innerText,
        }}
      />

      {isFiltersPresent && (
        <Button
          size="small"
          role="button"
          startIcon={
            filtersCount > 0 ? (
              <FilledFilterIcon
                sx={{ width: '40px', height: '40px' }}
                className={filtersCount > 0 ? classes.filterIcon : ''}
              />
            ) : (
              <FilterIcon
                sx={{ width: '40px', height: '40px' }}
                className={filtersCount > 0 ? classes.filterIcon : ''}
              />
            )
          }
          endIcon={filtersCount > 0 && <span className={classes.filtersCount}>{filtersCount}</span>}
          variant="outlined"
          onClick={handleFilterClick}
          sx={{
            textTransform: 'none !important',
            borderColor: filtersCount > 0 ? '#2863EB' : openFilters ? '#F67570 !important' : '#E1E3E8 !important',
            color: openFilters && filtersCount <= 0 ? '#F67570 !important' : '#30343F',
            backgroundColor: filtersCount > 0 ? '#E7EDFF' : '',
            fontFamily: 'Lato',
          }}
          className={classes.filtersButton}
        >
          Filters
        </Button>
      )}
    </form>
  );
};

export default SearchBox;
